export const TOKENIZABLE_ASSET_STATUSES = new Set([
  'ApprovedForTokenization',
  'approved_for_tokenization',
  'verified'
]);

export const MINTED_ASSET_STATUSES = new Set(['Minted', 'tokenized', 'minted']);

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Sequential offline txs: base nonce from first prepare + delta (1, 2, …). */
export const incrementNonceHex = (baseNonceHex, delta) => {
  const raw = String(baseNonceHex || '0x0').trim();
  const hexDigits = /^0x/i.test(raw) ? raw.slice(2) : raw;
  const base = Number.parseInt(hexDigits || '0', 16);
  const inc = Number(delta);
  const n = base + inc;
  if (!Number.isSafeInteger(n) || n < 0) {
    throw new Error('Nonce increment overflow or invalid delta.');
  }
  return `0x${n.toString(16)}`;
};

export const isSuccessfulReceiptStatus = (status) => {
  if (!status) {
    return false;
  }

  const normalized = String(status).toLowerCase();
  return normalized === '0x1' || normalized === '1' || normalized === 'confirmed' || normalized === 'success';
};

export const isFailedReceiptStatus = (status) => {
  if (!status) {
    return false;
  }

  const normalized = String(status).toLowerCase();
  return normalized === '0x0' || normalized === '0' || normalized === 'failed';
};

export const sanitizeContractName = (value = '') => {
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '');
  if (!cleaned) {
    return `PingAsset${Date.now()}`;
  }

  const startsWithLetter = /^[A-Za-z]/.test(cleaned);
  return startsWithLetter ? cleaned : `Ping${cleaned}`;
};

export const parseSignedTransactionInput = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    throw new Error('Signed transaction input is required.');
  }

  if (rawValue.startsWith('0x')) {
    return {
      signedTransactionHex: rawValue
    };
  }

  const parsed = JSON.parse(rawValue);
  if (Array.isArray(parsed)) {
    return parsed.map((entry) => normalizeSignedTransactionEntry(entry));
  }

  // Ping offline signer batch output: { batchSignedTransactions, results: [ { signedTransactionHex }, ... ] }
  const batchResults = parsed.results ?? parsed.Results;
  const hasRootHex = Boolean(parsed.signedTransactionHex ?? parsed.SignedTransactionHex);
  if (Array.isArray(batchResults) && batchResults.length > 0 && !hasRootHex) {
    return batchResults.map((entry) => normalizeSignedTransactionEntry(entry));
  }

  return normalizeSignedTransactionEntry(parsed);
};

const normalizeSignedTransactionEntry = (entry) => {
  const hex = entry?.signedTransactionHex ?? entry?.SignedTransactionHex;
  if (!hex || typeof hex !== 'string') {
    throw new Error('Signed transaction JSON must contain signedTransactionHex.');
  }

  return {
    signedTransactionHex: hex.trim(),
    expectedContractAddress: entry.expectedContractAddress ?? entry.ExpectedContractAddress ?? null,
    transactionHash: entry.transactionHash ?? entry.TransactionHash ?? null,
    signerAddress: entry.signerAddress ?? entry.SignerAddress ?? null
  };
};

export const formatPreparedPayload = (payload) => JSON.stringify(payload, null, 2);

/**
 * Map API prepared-tx DTO to a single camelCase shape (handles PascalCase or mixed responses).
 * @param {unknown} raw
 * @returns {object | null}
 */
export function normalizePreparedTransactionResponse(raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const data = raw.data ?? raw.Data;
  const chainId = raw.chainId ?? raw.ChainId;
  const from = raw.from ?? raw.From;
  const to = raw.to ?? raw.To;
  const value = raw.value ?? raw.Value;
  const nonce = raw.nonce ?? raw.Nonce;
  const gasLimit = raw.gasLimit ?? raw.GasLimit;
  const gasPrice = raw.gasPrice ?? raw.GasPrice;
  const summary = raw.summary ?? raw.Summary;
  const resolvedTokenId = raw.resolvedTokenId ?? raw.ResolvedTokenId;
  return {
    chainId: chainId != null ? String(chainId) : '',
    from: from != null ? String(from) : '',
    to: to != null && String(to).trim() ? String(to) : null,
    data: data != null ? String(data) : '',
    value: value != null ? String(value) : '0x0',
    nonce: nonce != null ? String(nonce) : '0x0',
    gasLimit: gasLimit != null ? String(gasLimit) : '0x0',
    gasPrice: gasPrice != null ? String(gasPrice) : '0x0',
    summary: summary != null ? String(summary) : '',
    resolvedTokenId: resolvedTokenId != null ? String(resolvedTokenId) : null
  };
}

export const copyTextToClipboard = async (value) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document !== 'undefined') {
    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
};

export const downloadJsonFile = (filename, data) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const blob = new Blob([content], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

export const buildSignerOutputTemplate = (overrides = {}) => ({
  signerAddress: '0xSignerAddress',
  signedTransactionHex: '0x...',
  transactionHash: '0x...',
  expectedContractAddress: null,
  summary: overrides.summary || 'Paste the output returned by the offline signer.',
  keySource: 'env:SMARTVEST_SIGNER_PRIVATE_KEY',
  signedAtUtc: new Date().toISOString(),
  ...overrides
});
